// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\analytics\ClinicStatsPage.js

import React, { useEffect, useState, useCallback } from "react";
import { 
  Row, Col, Card, Statistic, Table, DatePicker, 
  Button, Space, Typography, Progress, Tag, Select, message 
} from "antd";
import { 
  BarChartOutlined, GiftOutlined, UserOutlined, 
  MailOutlined, CalendarOutlined, ReloadOutlined, 
  DownloadOutlined
} from "@ant-design/icons";
import axiosInstance from "../../services/axiosServicesVisit";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const ClinicStatsPage = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ 
    samples: [], 
    births: [], 
    reports: [], 
    summary: { totalBirths: 0, emailsMarketing: 0, avgVisits: 0 } 
  });
  const [filters, setFilters] = useState({ startDate: null, endDate: null });
  const [clinics, setClinics] = useState([]);
  const [sampleFilters, setSampleFilters] = useState({ clinicId: null });

  // 1. CHARGEMENT DES RÉFÉRENCES (CLINIQUES)
  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const resClinics = await axiosInstance.get("/v1/report-clinics/clinics-list");
        setClinics(resClinics.data || []);
      } catch (e) { 
        message.error("Erreur lors du chargement des filtres."); 
      }
    };
    fetchReferences();
  }, []);

  // 2. FONCTION DE RÉCUPÉRATION DES DONNÉES
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const commonParams = {};
      if (filters.startDate && filters.endDate) {
        commonParams.startDate = filters.startDate;
        commonParams.endDate = filters.endDate;
        commonParams.dateInterval = `${filters.startDate},${filters.endDate}`;
      }

      // Appels API simultanés
      const [resSamples, resBirths, resReports, resSummary] = await Promise.all([
        axiosInstance.get("/v1/report-clinics/stats/samples", { params: { ...commonParams, ...sampleFilters } }),
        axiosInstance.get("/v1/report-clinics/stats/births", { params: commonParams }),
        axiosInstance.get("/v1/report-clinics", { params: { limit: 1000, ...commonParams } }),
        axiosInstance.get("/v1/report-clinics/stats/summary", { params: commonParams }).catch(() => ({ data: null }))
      ]);

      const reportsList = resReports.data?.data || [];
      
      setData({
        samples: resSamples.data || [],
        births: resBirths.data || [],
        reports: reportsList,
        summary: resSummary.data || {
            totalBirths: resReports.data?.total || 0,
            emailsMarketing: reportsList.filter(r => r.momEmail).length,
            avgVisits: filters.startDate ? (resReports.data?.total / (dayjs(filters.endDate).diff(dayjs(filters.startDate), 'day') + 1)).toFixed(1) : 0
        }
      });
    } catch (e) {
      console.error("Erreur fetchData:", e);
      message.error("Impossible de charger les statistiques.");
    } finally {
      setLoading(false);
    }
  }, [filters, sampleFilters]);

  // --- 3. DÉCLENCHEUR AUTOMATIQUE (AJOUTÉ) ---
  useEffect(() => {
    fetchData();
  }, [fetchData]); 
  // Se déclenche à chaque changement de date ou de filtre clinique

  // --- EXPORTS CSV ---
  const downloadCSV = (jsonData, headers, fileName) => {
    let str = '\uFEFF'; 
    str += headers.join(';') + '\r\n';
    jsonData.forEach(obj => {
      str += Object.values(obj).join(';') + '\r\n';
    });
    const blob = new Blob([str], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  const exportSamples = () => {
    const formatted = data.samples.map(s => ({ C: s.clinicName, E: s.sampleName, Q: s.totalQuantity }));
    downloadCSV(formatted, ["Clinique", "Echantillon", "Quantite"], "distribution_echantillons");
  };

  const exportBirths = () => {
    const formatted = data.births.map(b => ({ C: b.clinicName, N: b.count, P: b.ratio + "%" }));
    downloadCSV(formatted, ["Clinique", "Naissances", "Part de Marche"], "parts_de_marche");
  };

  const exportMarketingContacts = () => {
  // 1. Filtrer les rapports pour n'avoir que ceux avec un email
  const marketingData = data.reports
    .filter(r => r.momEmail && r.momEmail.trim() !== "")
    .map(r => ({
      Maman: r.momName || "N/A",
      Email: r.momEmail || "N/A",
      Bebe: r.babyName || "N/A",
      "Date Naissance": r.babyDob ? dayjs(r.babyDob).format("DD/MM/YYYY") : "N/A",
      Clinique: r.clinic?.name || "N/A"
    }));

  if (marketingData.length === 0) {
    message.warning("Aucun contact avec email à exporter.");
    return;
  }

  // 2. Définir les en-têtes du fichier
  const headers = ["Maman", "Email", "Bébé", "Date Naissance", "Clinique"];

  // 3. Utiliser la fonction de téléchargement existante (downloadCSV)
  downloadCSV(marketingData, headers, "contacts_marketing_babyworld");
};

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: 25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3}><BarChartOutlined /> Intelligence Maternité & Naissances</Title>
        <Space>
          <RangePicker 
            onChange={(d) => setFilters(d ? { startDate: d[0].format("YYYY-MM-DD"), endDate: d[1].format("YYYY-MM-DD") } : { startDate: null, endDate: null })} 
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Actualiser</Button>
        </Space>
      </div>

      {/* CARTES DE RÉSUMÉ */}
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card className="circlebox">
            <Statistic title="Total Naissances" value={data.summary.totalBirths} prefix={<UserOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card className="circlebox">
            <Statistic title="Emails Marketing" value={data.summary.emailsMarketing} valueStyle={{ color: '#3f8600' }} prefix={<MailOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card className="circlebox">
            <Statistic title="Visites Moyenne / Jour" value={data.summary.avgVisits} prefix={<CalendarOutlined />} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        {/* DISTRIBUTION ÉCHANTILLONS */}
        <Col span={14}>
          <Card 
            title={<><GiftOutlined /> Distribution des Échantillons</>} 
            extra={
              <Space>
                <Select placeholder="Clinique" style={{ width: 120 }} allowClear onChange={(v) => setSampleFilters({ clinicId: v })}>
                  {clinics.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
                <Button type="primary" icon={<DownloadOutlined />} onClick={exportSamples} size="small">Export</Button>
              </Space>
            }
          >
            <Table 
                loading={loading}
                dataSource={data.samples} 
                rowKey={(record) => `${record.clinicName}-${record.sampleName}`}
                size="small" 
                pagination={{ pageSize: 5 }} 
                columns={[
                    { title: "Clinique", dataIndex: "clinicName" }, 
                    { title: "Échantillon", dataIndex: "sampleName" }, 
                    { title: "Total", dataIndex: "totalQuantity", render: (q) => <Tag color="green">{q}</Tag> }
                ]} 
            />
          </Card>
        </Col>

        {/* PART DE MARCHÉ */}
        <Col span={10}>
          <Card 
            title={<><UserOutlined /> Part de Marché</>}
            extra={<Button type="primary" icon={<DownloadOutlined />} onClick={exportBirths} size="small">Export</Button>}
          >
            <Table 
                loading={loading}
                dataSource={data.births} 
                rowKey="clinicName"
                size="small" 
                pagination={false} 
                columns={[
                    { title: "Clinique", dataIndex: "clinicName" }, 
                    { title: "Naissances", dataIndex: "count" }, 
                    { title: "%", dataIndex: "ratio", render: (r) => <Progress percent={parseFloat(r)} size="small" /> }
                ]} 
            />
          </Card>
        </Col>
      </Row>

      {/* TABLE MARKETING */}
      <Card 
        title={<><MailOutlined /> Base de données Marketing</>} 
        style={{ marginTop: 20 }}

        extra={
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={exportMarketingContacts}
            disabled={data.reports.filter(r => r.momEmail).length === 0}
          >
            Exporter la liste (CSV)
          </Button>
        }
      >
        <Table 
          loading={loading}
          dataSource={data.reports.filter(r => r.momEmail)} 
          rowKey="id"
          size="small" 
          columns={[
            { title: "Maman", dataIndex: "momName" },
            { title: "Email", dataIndex: "momEmail", render: (e) => <Text copyable>{e}</Text> },
            { title: "Bébé", dataIndex: "babyName" },
            { title: "Né le", dataIndex: "babyDob", render: (d) => dayjs(d).format("DD/MM/YYYY") },
            { title: "Clinique", dataIndex: ["clinic", "name"] }
          ]}
        />
      </Card>
    </div>
  );
};

export default ClinicStatsPage;